import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useFormikContext } from 'formik';
import { Button } from '../../../button/Button';
import { SourceAPI } from '../../../sources/api/SourceAPI';
import { Banner } from '../../../banner/Banner';
import { TemplateFormFieldProps } from '../../interfaces/template-form';
import { TemplateFormAuthorizationButtonFieldMetaProps } from './interfaces/template-form-auth-button-field-meta';
import { getDataIdGenerator } from '../../../shared/utils/dataId';

const dataIdGenerator = getDataIdGenerator('authorization-button-field');

export function TemplateFormAuthorizationButtonField({
  metadata,
  field,
  form
}: TemplateFormFieldProps) {
  const [error, setError] = useState('');
  const [createdTs, setCreatedTs] = useState<number>();
  const [authWindow, setAuthWindow] = useState<Window | null>(null);

  const { errors: formErrors } = useFormikContext();

  const { dataId, label, fortressUrl, provider, ...authButtonProps } =
    metadata as TemplateFormAuthorizationButtonFieldMetaProps;

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'created_ts' && event.newValue) {
        setCreatedTs(Number(event.newValue));
      } else if (event.key === 'redirectErrorMessage' && event.newValue) {
        setError(event.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const effectiveCreatedTs = Number(createdTs);
    if (!effectiveCreatedTs) return; // Early return if no timestamp is available

    const fetchToken = async () => {
      try {
        const data = await SourceAPI.getToken(fortressUrl, provider, effectiveCreatedTs);
        form.setFieldValue(field.name, data.data.id);
        form.setFieldValue('authenticated_user', data.data.authenticated_user);
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        }
      } finally {
        localStorage.removeItem('created_ts');
      }
    };

    fetchToken();
  }, [createdTs]);

  const handleAuthorizeAccount = () => {
    setError('');

    // Closing previous window if it exists
    if (authWindow && !authWindow.closed) {
      authWindow.close();
    }

    const redirectUri = `${window.location.origin}/oauthRedirection.html`;
    SourceAPI.getConsentUrl(fortressUrl, provider, redirectUri, redirectUri)
      .then(response => {
        let childUrl = response.data.urls[0]?.url;

        if (form.values?.account_type) {
          childUrl = response.data?.urls?.find(
            url => url.env === form.values?.account_type.toUpperCase()
          )?.url as string;
        }

        const newWindow = window.open(childUrl, 'authWindow');

        if (newWindow) {
          newWindow.focus();
          setAuthWindow(newWindow);
        } else {
          // eslint-disable-next-line no-alert
          window.alert('Popup window blocked! Please allow popups for this site.');
        }
      })
      .catch(err => {
        setError(err.message);
      });
  };

  function renderContent() {
    const authorizedUser = form.values.authenticated_user;

    if (authorizedUser) {
      return (
        <div className='d-flex align-items-center'>
          <div className={clsx('border-dotted', 'd-flex align-items-center p-2 mr-2')}>
            <span className='text-bold'>Account Added:</span>
            <span className='text-secondary ml-2'>{authorizedUser}</span>
          </div>

          <Button
            type='button'
            icon='placeholder'
            direction='left'
            variation='outline'
            size='md'
            dataId={dataIdGenerator('reauthorization')}
            onClick={handleAuthorizeAccount}
          >
            Reauthorize
          </Button>
        </div>
      );
    }

    return (
      <>
        <Button
          type='button'
          dataId={dataIdGenerator('authorization')}
          onClick={handleAuthorizeAccount}
        >
          {label}
        </Button>

        {field.name in formErrors && field.name in form.touched ? (
          <span className='ml-4 text-body-3 text-bold text-error'>Authorization required</span>
        ) : null}

        {error ? (
          <Banner
            className='mt-5'
            open
            dataId={dataIdGenerator('error-banner')}
            color='error'
            icon='error-outline'
          >
            {error}
          </Banner>
        ) : null}
      </>
    );
  }

  return <div {...authButtonProps}>{renderContent()}</div>;
}
